import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  ArrowLeft, Clock, BookOpen, CheckCircle2, Circle, 
  Play, Lock, Loader2, Download, Award, CreditCard, HelpCircle
} from 'lucide-react';
import ShareButtons from '../components/ShareButtons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrainingCourseDetail = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    fetchCourse();
    
    // Check for purchase redirect
    if (searchParams.get('purchased') === 'true') {
      verifyPurchase();
    }
  }, [slug, searchParams]);

  const fetchCourse = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/training/courses/${slug}`, { headers });
      setCourse(response.data);
    } catch (error) {
      console.error('Failed to fetch course:', error);
      if (error.response?.status === 404) {
        navigate('/training');
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyPurchase = async () => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && token) {
      try {
        await axios.post(`${API}/training/purchases/verify?session_id=${sessionId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Purchase successful! You now have full access.');
        fetchCourse();
      } catch (error) {
        console.error('Verify error:', error);
      }
    }
  };

  const handlePurchase = async () => {
    if (!user) {
      toast.error('Please log in to purchase');
      navigate('/login');
      return;
    }

    setPurchasing(true);
    try {
      const response = await axios.post(`${API}/training/courses/${course.id}/purchase`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purchase failed');
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!course) return null;

  const progressPercentage = course.total_lessons > 0 
    ? Math.round((course.completed_count / course.total_lessons) * 100) 
    : 0;

  const getFirstIncompleteLesson = () => {
    return course.lessons.find(l => !l.completed) || course.lessons[0];
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* Hero */}
      <div className="pt-24 pb-8 bg-gradient-to-br from-primary/10 via-purple-50 to-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <Button variant="ghost" onClick={() => navigate('/training')} className="mb-4 -ml-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            All Courses
          </Button>
          
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Course Info */}
            <div className="flex-1">
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge className="bg-green-100 text-green-800">Free</Badge>
                <Badge variant="outline">{course.level}</Badge>
              </div>
              
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                {course.title}
              </h1>
              <p className="text-lg text-slate-600 mb-4">
                {course.description}
              </p>
              <div className="mb-6">
                <ShareButtons title={course.title} description={course.description} />
              </div>

              <div className="flex flex-wrap items-center gap-6 text-slate-600 mb-6">
                <span className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  {course.total_lessons} lessons
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  {course.total_duration_minutes} minutes
                </span>
                <span className="flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  Certificate included
                </span>
              </div>

              {/* Progress */}
              {user && course.completed_count > 0 && course.has_access && (
                <div className="bg-white rounded-lg p-4 border border-slate-200 mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-slate-900">Your Progress</span>
                    <span className="text-primary font-semibold">{progressPercentage}%</span>
                  </div>
                  <Progress value={progressPercentage} className="h-3" />
                  <p className="text-sm text-slate-500 mt-2">
                    {course.completed_count} of {course.total_lessons} lessons completed
                  </p>
                </div>
              )}

              {/* CTA Button */}
              {course.has_access ? (
                <Button 
                  size="lg"
                  onClick={() => {
                    const lesson = getFirstIncompleteLesson();
                    navigate(`/training/lesson/${lesson.slug}`);
                  }}
                  className="bg-primary hover:bg-primary/90"
                >
                  <Play className="w-5 h-5 mr-2" />
                  {course.completed_count > 0 ? 'Continue Learning' : 'Start Course'}
                </Button>
              ) : (
                <div className="space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-amber-800">
                      <Lock className="w-5 h-5" />
                      <span className="font-medium">
                        {course.is_free ? 'Login required' : `Unlock for $${course.price}`}
                      </span>
                    </div>
                    <p className="text-sm text-amber-700 mt-1">
                      {course.is_free 
                        ? 'Log in to access all lessons and track your progress.'
                        : 'Purchase this course to unlock all lessons and get your certificate.'
                      }
                    </p>
                  </div>
                  {course.is_free ? (
                    <Button size="lg" onClick={() => navigate('/login')}>
                      Log In to Start
                    </Button>
                  ) : (
                    <Button 
                      size="lg" 
                      onClick={handlePurchase}
                      disabled={purchasing}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {purchasing ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <CreditCard className="w-5 h-5 mr-2" />
                      )}
                      Unlock Course - ${course.price}
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Thumbnail */}
            <div className="lg:w-96">
              <img 
                src={course.thumbnail_url}
                alt={course.title}
                className="w-full h-64 lg:h-full object-cover rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Lessons List */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              Course Curriculum
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-slate-100">
              {course.lessons.map((lesson, index) => (
                <div 
                  key={lesson.id}
                  className={`py-4 flex items-center gap-4 ${
                    lesson.completed ? 'opacity-75' : ''
                  } ${lesson.locked ? 'opacity-60' : ''}`}
                >
                  {/* Status Icon */}
                  <div className="flex-shrink-0">
                    {lesson.locked ? (
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                        <Lock className="w-5 h-5 text-slate-400" />
                      </div>
                    ) : lesson.completed ? (
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                        <span className="text-sm font-semibold text-slate-500">{index + 1}</span>
                      </div>
                    )}
                  </div>

                  {/* Lesson Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className={`font-medium truncate ${lesson.locked ? 'text-slate-500' : 'text-slate-900'}`}>
                        {lesson.title}
                      </h3>
                      <Badge variant="outline" className="text-xs flex-shrink-0">
                        {lesson.tool_name}
                      </Badge>
                      {lesson.is_preview && !course.has_access && (
                        <Badge className="bg-blue-100 text-blue-800 text-xs">Preview</Badge>
                      )}
                      {lesson.has_quiz && (
                        <Badge variant="outline" className="text-xs flex items-center gap-1">
                          <HelpCircle className="w-3 h-3" />
                          Quiz
                        </Badge>
                      )}
                      {lesson.quiz_passed && (
                        <Badge className="bg-green-100 text-green-800 text-xs">Quiz Passed</Badge>
                      )}
                    </div>
                    <p className={`text-sm truncate ${lesson.locked ? 'text-slate-400' : 'text-slate-500'}`}>
                      {lesson.short_summary}
                    </p>
                  </div>

                  {/* Duration & Action */}
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <span className="text-sm text-slate-500 flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {lesson.duration_minutes} min
                    </span>
                    {lesson.locked ? (
                      <Button variant="outline" size="sm" disabled>
                        <Lock className="w-4 h-4 mr-1" />
                        Locked
                      </Button>
                    ) : (
                      <Button
                        variant={lesson.completed ? 'outline' : 'default'}
                        size="sm"
                        onClick={() => navigate(`/training/lesson/${lesson.slug}`)}
                      >
                        {lesson.completed ? 'Review' : 'Start'}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* What You'll Learn */}
        <div className="mt-12 grid md:grid-cols-2 gap-8">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Tools You'll Master</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {course.lessons.map((lesson) => (
                  <Badge key={lesson.id} variant="outline" className="px-3 py-1">
                    {lesson.tool_name}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading text-lg">What's Included</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {[
                  'Step-by-step tutorials for each tool',
                  'Copy/paste templates and prompts',
                  '15-minute hands-on mini projects',
                  'Common mistakes and how to fix them',
                  'Quizzes to test your knowledge',
                  'Completion certificate'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TrainingCourseDetail;
